import { useEffect, useState } from 'react';
import UserDataService from '../services/user-data.service';
import useBrowserStorage from '../hooks/use-browser-storage';
import { useAppContext } from '../utils/context/app-context';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/auth.service';
import DateRangeFilter, { DateRangeType } from './common/DateRangeFilter';

const Dashboard = () => {
  const [stats, setStats] = useState<{
    userCount: number;
    userComparision: number;
    qaCount: number;
    qaComparision: number;
    subscriptionCount: number;
    subsComparision: number;
  }>();
  const [dates, setDates] = useState<DateRangeType>({
    startDate: undefined,
    endDate: undefined,
  });
  const [search, setSearch] = useState(false);
  const { getItem, setItem } = useBrowserStorage();
  const { setUserInfo, userInfo } = useAppContext();
  const navigate = useNavigate();

  const getStats = async () => {
    const input: any = {
      startDate: dates.startDate,
      endDate: dates.endDate,
    };
    try {
      const response = await UserDataService.getDashboardStats(input);
      if (response.data.data) {
        setStats(response.data.data);
        setSearch(false);
      } else {
        console.log('Error: ', response.data.message);
      }
    } catch (err) {
      console.log('Error: ', err);
    }
  };

  useEffect(() => {
    getStats();
  }, []);

  useEffect(() => {
    if (search && dates.startDate && dates.endDate) getStats();
  }, [dates, search]);

  const checkSession = async () => {
    try {
      const sessionId = getItem('sessionId', 'local');
      if (!sessionId) {
        setItem('userInfo', 'null', 'local');
        setItem('sessionId', '', 'local');
        setUserInfo(null);

        navigate('/');
        return;
      }
      const response = await AuthService.checkSession(sessionId ?? '');
      if (response.data.error) {
        setItem('userInfo', 'null', 'local');
        setItem('sessionId', '', 'local');
        setUserInfo(null);
        navigate('/');
      }
    } catch (error: any) {
      console.log('Error in Session Check: ', error);
      if (error?.response && error?.response?.status === 401) {
        setItem('userInfo', 'null', 'local');
        setItem('sessionId', '', 'local');
        setUserInfo(null);
      }
    }
  };

  useEffect(() => {
    const sessionId = getItem('sessionId', 'local');
    if (!userInfo?._id || !sessionId) return;
    setInterval(() => {
      checkSession();
    }, 1800000);
  }, [userInfo?._id]);

  useEffect(() => {
    const sessionId = getItem('sessionId', 'local');
    if (!userInfo?._id || !sessionId) return;
    checkSession();
  }, []);

  return (
    <div className="flex grow flex-col">
      <div className="flex justify-between items-center w-full p-4 bg-white border-b border-[#ddd]">
        <span className="text-[25px] font-semibold">Dashboard</span>
        <div className="flex items-center">
          <DateRangeFilter
            setDateRange={(values) => setDates(values)}
            dateRange={dates}
          />
        </div>
      </div>
      <div className="flex items-start grow">
        <div className="flex flex-wrap justify-between items-start grow">
          <div className="p-5 w-1/3">
            <div className="p-6 min-h-[150px] w-full border border-[#f3f3f3] rounded-md">
              <h3 className="font-bold text-[25px]">Users</h3>
              <p className="text-[25px] md:text-[30px] lg:text-[40px] xl:text-[50px]">
                {stats?.userCount ?? 0}{' '}
                <span className="text-green-500 text-[18px] md:text-[20px] lg:text-[27px] xl:text-[35px]">
                  {stats?.userComparision.toFixed(2)}%
                </span>
              </p>
            </div>
          </div>
          <div className="p-5 w-1/3">
            <div className="p-6 min-h-[150px] w-full border border-[#f3f3f3] rounded-md">
              <h3 className="font-bold text-[25px]">Subscriptons</h3>
              <p className="text-[25px] md:text-[30px] lg:text-[40px] xl:text-[50px]">
                {stats?.subscriptionCount ?? 0}{' '}
                <span className="text-green-500 text-[18px] md:text-[20px] lg:text-[27px] xl:text-[35px]">
                  {stats?.subsComparision.toFixed(2)}%
                </span>
              </p>
            </div>
          </div>
          <div className="p-5 w-1/3">
            <div className="p-6 min-h-[150px] w-full border border-[#f3f3f3] rounded-md">
              <h3 className="font-bold text-[25px]">Queries</h3>
              <p className="text-[25px] md:text-[30px] lg:text-[40px] xl:text-[50px]">
                {stats?.qaCount ?? 0}{' '}
                <span className="text-green-500 text-[18px] md:text-[20px] lg:text-[27px] xl:text-[35px]">
                  {stats?.qaComparision.toFixed(2)}%
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;